import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { authAPI } from '../../services/api';
import awtfLogo from '../../assets/awtf_logo.png';

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  );
}

function SmsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
    </svg>
  );
}

function EmailLoginTab({ onSuccess }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const { login, loading, error, clearError } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    const success = await login(email, password);
    if (success) onSuccess();
  };

  if (showForgot) {
    return <ForgotPasswordFlow onBack={() => setShowForgot(false)} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <div>
        <label className="mb-2 block text-sm font-semibold text-slate-700">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => { clearError(); setEmail(e.target.value); }}
          className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
          placeholder="your@email.com"
          required
        />
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-sm font-semibold text-slate-700">Password</label>
          <button type="button" onClick={() => setShowForgot(true)}
            className="text-xs font-semibold text-orange-500 hover:underline">
            Forgot password?
          </button>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => { clearError(); setPassword(e.target.value); }}
          className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
          placeholder="Your password"
          required
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {loading ? 'Logging in...' : 'Login'}
      </button>
    </form>
  );
}

function ForgotPasswordFlow({ onBack }) {
  const [step, setStep]           = useState(1); // 1=email+method, 2=token+password, 3=done
  const [email, setEmail]         = useState('');
  const [method, setMethod]       = useState('sms'); // 'sms' | 'email'
  const [via, setVia]             = useState('sms');
  const [token, setToken]         = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const inputCls = 'w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20';
  const btnCls   = 'w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0';

  async function handleSendToken(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.forgotPassword(email, method);
      setVia(res.via || method);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send token. Check your email address.');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword(email, token, newPassword);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired token.');
    } finally {
      setLoading(false);
    }
  }

  if (step === 3) {
    return (
      <div className="space-y-4 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-2xl">✓</div>
        <p className="font-semibold text-slate-800">Password reset successfully!</p>
        <p className="text-sm text-slate-500">You can now log in with your new password.</p>
        <button onClick={onBack} className={btnCls}>Back to Login</button>
      </div>
    );
  }

  if (step === 2) {
    const hint = via === 'email'
      ? 'If this email is registered, a 4-digit token will be sent to it. Check your inbox and spam folder. If nothing arrives, check that you entered the email used for your account.'
      : 'If this email is registered, a 4-digit token will be sent to the phone number linked to your account via SMS. If nothing arrives, check that you entered the email used for your account.';
    return (
      <form onSubmit={handleReset} className="space-y-4">
        <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          {hint} Enter it below together with your new password.
        </div>
        {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Reset Token (4 digits)</label>
          <input type="text" value={token} maxLength={4}
            onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-slate-900 shadow-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
            placeholder="0000" required />
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">New Password</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            className={inputCls} placeholder="Min. 6 characters" required />
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Confirm Password</label>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputCls} placeholder="Repeat new password" required />
        </div>
        <button type="submit" disabled={loading || token.length !== 4} className={btnCls}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
        <button type="button" onClick={() => { setStep(1); setError(''); setToken(''); }}
          className="w-full rounded-2xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-600 hover:bg-slate-50">
          Back
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSendToken} className="space-y-4">
      <p className="text-sm text-slate-500">
        Enter your registered email and choose how you want to receive the reset token.
      </p>
      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div>
        <label className="mb-2 block text-sm font-semibold text-slate-700">Email Address</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className={inputCls} placeholder="your@email.com" required />
      </div>

      <div>
        <label className="mb-2 block text-sm font-semibold text-slate-700">Recovery Method</label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: 'sms',   label: 'SMS',   desc: 'Token via phone' },
            { value: 'email', label: 'Email', desc: 'Token via email' },
          ].map(({ value, label, desc }) => (
            <button
              key={value}
              type="button"
              onClick={() => setMethod(value)}
              className={`flex flex-col items-center rounded-2xl border-2 py-3 px-2 text-sm font-semibold transition-colors ${
                method === value
                  ? 'border-orange-500 bg-orange-50 text-orange-600'
                  : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
              }`}
            >
              <span>{label}</span>
              <span className="text-xs font-normal mt-0.5 opacity-70">{desc}</span>
            </button>
          ))}
        </div>
      </div>

      <button type="submit" disabled={loading} className={btnCls}>
        {loading ? 'Sending...' : `Send Token via ${method === 'sms' ? 'SMS' : 'Email'}`}
      </button>
      <button type="button" onClick={onBack}
        className="w-full rounded-2xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-600 hover:bg-slate-50">
        Back to Login
      </button>
    </form>
  );
}

function SmsLoginTab({ onSuccess }) {
  const [step, setStep] = useState(1);
  const [rollNo, setRollNo] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const { requestSmsToken, loginWithSmsToken, loading, error, clearError } = useAuthStore();

  const handleRequestToken = async (e) => {
    e.preventDefault();
    clearError();
    const success = await requestSmsToken(rollNo, phone);
    if (success) setStep(2);
  };

  const handleVerifyToken = async (e) => {
    e.preventDefault();
    clearError();
    const success = await loginWithSmsToken(rollNo, otp);
    if (success) onSuccess();
  };

  const handleBack = () => {
    clearError();
    setStep(1);
    setOtp('');
  };

  if (step === 2) {
    return (
      <form onSubmit={handleVerifyToken} className="space-y-4">
        <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          A 4-digit token has been sent to your phone number. It expires in 10 minutes.
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">SMS Token (4 digits)</label>
          <input
            type="text"
            value={otp}
            onChange={(e) => { clearError(); setOtp(e.target.value.replace(/\D/g, '').slice(0, 4)); }}
            className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-slate-900 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
            placeholder="0000"
            maxLength={4}
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading || otp.length !== 4}
          className="w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {loading ? 'Verifying...' : 'Verify Token'}
        </button>

        <button
          type="button"
          onClick={handleBack}
          className="w-full rounded-2xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
        >
          Back
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleRequestToken} className="space-y-4">
      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <div>
        <label className="mb-2 block text-sm font-semibold text-slate-700">Roll Number</label>
        <input
          type="text"
          value={rollNo}
          onChange={(e) => { clearError(); setRollNo(e.target.value); }}
          className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
          placeholder="Roll number"
          required
        />
      </div>
      <div>
        <label className="mb-2 block text-sm font-semibold text-slate-700">Phone Number</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => { clearError(); setPhone(e.target.value); }}
          className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20"
          placeholder="0712 345 678"
          required
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {loading ? 'Sending token...' : 'Send SMS Token'}
      </button>
    </form>
  );
}

export default function Login() {
  const [tab, setTab] = useState('email');
  const navigate = useNavigate();
  const { clearError } = useAuthStore();

  const handleSuccess = () => navigate('/', { replace: true });

  const switchTab = (t) => {
    clearError();
    setTab(t);
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(145deg,#1b2857_0%,#4a1a06_45%,#f07415_100%)] flex items-center justify-center px-4">

      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute left-[-6rem] top-[-6rem] h-[22rem] w-[22rem] rounded-full bg-[#f07415]/20 blur-3xl" />
        <div className="absolute right-[-6rem] bottom-[-6rem] h-[22rem] w-[22rem] rounded-full bg-[#1b2857]/40 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">

        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-[28px] bg-white/95 p-3 shadow-[0_16px_40px_rgba(0,0,0,0.3)] ring-1 ring-white/30 backdrop-blur">
            <img
              src={awtfLogo}
              alt="AWTF Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-white">AWTF Portal</h1>
          <p className="mt-1 text-sm text-orange-100/80">Advocates Welfare Trust Fund</p>
          <p className="mt-2 text-xs font-medium uppercase tracking-[0.22em] text-orange-100/60">
            Login for members and admins
          </p>
        </div>

        <div className="overflow-hidden rounded-[32px] border border-white/12 bg-white shadow-[0_40px_80px_rgba(0,0,0,0.4)]">

          <div className="flex border-b border-slate-100">
            <button
              onClick={() => switchTab('sms')}
              className={`flex flex-1 items-center justify-center gap-2 py-4 text-sm font-semibold transition-colors ${
                tab === 'sms'
                  ? 'border-b-2 border-orange-500 text-orange-600 bg-orange-50/60'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <SmsIcon />
              SMS Token
            </button>
            <button
              onClick={() => switchTab('email')}
              className={`flex flex-1 items-center justify-center gap-2 py-4 text-sm font-semibold transition-colors ${
                tab === 'email'
                  ? 'border-b-2 border-orange-500 text-orange-600 bg-orange-50/60'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <LockIcon />
              Email
            </button>
          </div>

          <div className="p-6">
            {tab === 'email' ? (
              <>
                <p className="mb-5 text-sm text-slate-500">Sign in with your email and password.</p>
                <EmailLoginTab onSuccess={handleSuccess} />
              </>
            ) : (
              <>
                <p className="mb-5 text-sm text-slate-500">
                  Enter your roll number and registered phone number to receive a one-time token via SMS.
                </p>
                <SmsLoginTab onSuccess={handleSuccess} />
              </>
            )}
          </div>
        </div>

        <p className="mt-5 text-center text-sm">
          <Link to="/support/new" className="font-semibold text-orange-200 hover:text-white hover:underline">
            Having trouble? Contact Support
          </Link>
        </p>

        <p className="mt-4 text-center text-xs text-orange-100/50">
          &copy; {new Date().getFullYear()} Advocates Welfare Trust Fund
        </p>
      </div>
    </div>
  );
}
